/**
 * <cba-cart-drawer> — opens and closes the cart drawer (sections/cba-cart-drawer).
 * The cart inside it is <cba-cart-items> (assets/cba-cart-items.js); every
 * write to the cart is @theme/cart's (DESIGN-PLAN 7.G2).
 *
 *   <cba-cart-drawer data-cba-cart-section="…">
 *     <cba-dialog name="cart"><dialog id="cba-cart-drawer">
 *       <cba-cart-items …>…</cba-cart-items>
 *     </dialog></cba-dialog>
 *   </cba-cart-drawer>
 *
 *   document.querySelector('cba-cart-drawer').open(trigger)  // or openCart() from @theme/cart
 *
 * - Opening and closing are the shared <cba-dialog>'s (plan 3.5): the page
 *   behind is inert, the X, Escape and the backdrop close, focus returns to
 *   the trigger. Links to the cart page open the drawer instead, and so does
 *   ?cart=open on any page (then the parameter is removed). The cart page
 *   itself has no drawer: it is the cart.
 * - Adding to the cart does not open it: cba-cart-feedback shows the
 *   added-to-cart popup instead. The drawer's cart re-renders on the add,
 *   so it is current when opened.
 */
if (!customElements.get('cba-cart-drawer')) {
  class CbaCartDrawer extends HTMLElement {
    connectedCallback() {
      this.host = this.querySelector('cba-dialog');
      this.abort = new AbortController();
      const { signal } = this.abort;

      /* Links to the cart page open the drawer. */
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href]');
        if (link && link.origin === window.location.origin && link.pathname === window.routes.cart_url) {
          e.preventDefault();
          this.open(link);
        }
      }, { signal });

      this.openFromUrl();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
    }

    /* The element is defined by a deferred script, so an early open waits
       for <cba-dialog>. One overlay at a time: search, menu and the shop
       menu close; the quick-add sheet stacks over the cart. */
    open(trigger) {
      if (!this.host) return;
      customElements.whenDefined('cba-dialog').then(() => this.host.open(trigger));
    }

    close() { if (this.host && this.host.close) this.host.close(); }

    get isOpen() { return !!(this.host && this.host.isOpen); }

    /* ?cart=open lands a campaign, email or ad link on any page with the
       drawer open, e.g. /collections/all-rings?cart=open. A parameter, not a
       path like /collections/all-rings/cart: that is Shopify's legacy
       collection tag route, which redirects when no product has the tag
       "cart". The parameter is removed afterwards, so a refresh or a copied
       address does not open the drawer again. */
    openFromUrl() {
      let params;
      try { params = new URLSearchParams(window.location.search); } catch (e) { return; }
      const value = params.get('cart');
      if (value !== 'open' && value !== '1') return;
      this.open();
      try {
        params.delete('cart');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : '') + window.location.hash);
      } catch (e) { /* history blocked: the parameter stays */ }
    }
  }

  customElements.define('cba-cart-drawer', CbaCartDrawer);
}
